import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Spinner } from '../components/states';
import { Role } from './roles';
import { signOut, useAutoSignIn } from './session';
import { usePrincipal } from './usePrincipal';
import { useRoles } from './useRoles';

/**
 * Sign-in and the no-role case.
 *
 * Arriving unauthenticated redirects to Entra immediately, with no button in between; the
 * loop guards that make that safe are in `session.ts`. The sign-in screen below is only
 * reached when a redirect has been deliberately withheld, so it always carries a reason —
 * an unexplained "please sign in" would be worse than the button it replaced.
 *
 * The `Role.NONE` case is handled separately and deliberately: a user who authenticates
 * successfully but holds no app role is the single most common first-run outcome, and a blank
 * dashboard with 403s on every panel tells them nothing. Entra will happily issue a valid
 * token to any account in the tenant — as `api/auth.py` notes, authentication is not
 * authorisation — so this state is expected, not exceptional.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const principal = usePrincipal();
  const { blocked, signIn } = useAutoSignIn();
  const roles = useRoles();

  if (!isAuthenticated && blocked === null) {
    return (
      <Centered>
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner />
          {inProgress === InteractionStatus.None
            ? 'Redirecting to Microsoft…'
            : 'Signing in…'}
        </div>
      </Centered>
    );
  }

  if (!isAuthenticated) {
    return (
      <Centered>
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand">
            <ShieldCheckIcon className="h-6 w-6 text-on-brand" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-body">IRA</h1>
            <p className="mt-1 text-sm text-muted">{signInMessage(blocked)}</p>
          </div>
          {blocked !== 'signed-out' && blocked !== 'no-session' && (
            <Callout tone="warning" role="alert">
              <p className="break-words font-mono text-xs">{blocked}</p>
            </Callout>
          )}
          <Button variant="primary" size="md" className="w-full" onClick={signIn}>
            Sign in with Entra ID
          </Button>
        </div>
      </Centered>
    );
  }

  // Roles arrive one token acquisition after the account does. Without this the no-role
  // screen renders for everyone on every load and then disappears, which reads as a fault.
  if (roles.status === 'loading') {
    return (
      <Centered>
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner />
          Checking your access…
        </div>
      </Centered>
    );
  }

  if (roles.status === 'error') {
    return (
      <Centered>
        <div className="w-full max-w-lg space-y-4">
          <Callout tone="warning" role="alert">
            <p className="font-medium">Your access could not be read</p>
            <p className="mt-1 opacity-90">{roles.error}</p>
          </Callout>
          <div className="flex justify-center">
            <Button variant="secondary" size="sm" onClick={() => signOut(instance)}>
              Sign out and try again
            </Button>
          </div>
        </div>
      </Centered>
    );
  }

  if (principal.role === Role.NONE) {
    return (
      <Centered>
        <div className="w-full max-w-lg space-y-4">
          <Callout tone="warning" role="alert">
            <p className="font-medium">You have no role on this platform</p>
            <p className="mt-1 opacity-90">
              You signed in as{' '}
              <span className="font-mono">{principal.upn || principal.subject}</span>, but your
              token carries no IRA app role, so every route will refuse.
            </p>
            <p className="mt-2 opacity-90">
              An administrator needs to assign one of{' '}
              <span className="font-mono">IRA.Viewer</span>,{' '}
              <span className="font-mono">IRA.Analyst</span>,{' '}
              <span className="font-mono">IRA.Responder</span> or{' '}
              <span className="font-mono">IRA.Admin</span> on the API app registration. Sign out
              and back in once it is assigned — roles are baked into the token at issue.
            </p>
          </Callout>
          <div className="flex justify-center">
            <Button variant="secondary" size="sm" onClick={() => signOut(instance)}>
              Sign out
            </Button>
          </div>
        </div>
      </Centered>
    );
  }

  return <>{children}</>;
}

/**
 * The two withheld-redirect cases are expected and get plain prose. Anything else is an Entra
 * refusal whose own text is shown verbatim above the retry, because the AADSTS code is the
 * only part that identifies which registration setting is wrong.
 */
function signInMessage(blocked: string | null): string {
  if (blocked === 'signed-out') {
    return 'You have signed out. Sign in again to return to the dashboard.';
  }
  if (blocked === 'no-session') {
    return 'Microsoft returned without signing you in. Try again, or check that this account may use the dashboard.';
  }
  return 'Microsoft refused the sign-in.';
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-sunken p-6">{children}</div>
  );
}
